import React, { ReactNode, useEffect, useRef, useState } from 'react';

import { useUploadContext } from './UploadContext';

interface PictureUploadFile {
  path: string;
}

export interface PictureUpload {
  file: PictureUploadFile;
  url: string;
  thumbnail: string;
  uploading: boolean;
  failed: boolean;
  questionId: string;
  isCover?: boolean;
  placeholderName?: string;
}

type PicturesContextType = {
  deletePicture: (photo: PictureUpload) => void;
  pictures: PictureUpload[];
  lastSubmittedPictures: React.MutableRefObject<PictureUpload[]>;
  updateLastSubmittedPictures: () => void;
  restoreSavedPictures: () => void;
  savePicture: (photo: PictureUploadFile, questionId: string, placeholderName?: string, web?: boolean) => void;
  setCoverPicture: (questionId: string, picture?: PictureUpload) => void;
  retryPictureUpload: (photo: PictureUploadFile, questionId: string) => void;
  retryAllFailedPicturesUpload: () => void;
  getQuestionPictures: (pictures: PictureUpload[], questionId: string) => PictureUpload[];
  getLastQuestionPictures: (questionId: string) => PictureUpload[];
  getFailedPictures: () => PictureUpload[];
  getQuestionFailedPictures: (questionId: string) => PictureUpload[];
  getNumberOfPicturesUploading: () => number;
  getNumberOfPicturesFailed: () => number;
  setPicturesFromRetrievedSubmission: (pictures: PictureUpload[]) => void;
};

export const PicturesContext = React.createContext(undefined as PicturesContextType);

interface PicturesProviderProps {
  preloadedPictures?: PictureUpload[];
  children: ReactNode;
}

export const PicturesProvider = ({ children, preloadedPictures }: PicturesProviderProps) => {
  const [pictures, setPictures] = useState<PictureUpload[]>([]);
  const lastSubmittedPictures = useRef<PictureUpload[]>([]);
  const { uploadFile } = useUploadContext();

  const uploadPicture = async (picture: PictureUpload): Promise<void> => {
    try {
      picture.url = await uploadFile(picture.url);
    } catch (err) {
      picture.failed = true;
    } finally {
      picture.uploading = false;
      setPictures((pics) => [...pics]);
    }
  };

  const updateLastSubmittedPictures = () => {
    lastSubmittedPictures.current = pictures;
  };

  const restoreSavedPictures = () => {
    setPictures(lastSubmittedPictures.current);
  };

  const savePicture = (photo: PictureUploadFile, questionId: string, placeholderName?: string, web?: boolean) => {
    const localUrl = web ? photo.path : `file://${photo.path}`;

    const picture: PictureUpload = {
      file: photo,
      url: localUrl,
      thumbnail: localUrl,
      uploading: true,
      failed: false,
      questionId,
    };

    if (placeholderName) {
      picture.placeholderName = placeholderName;
    }

    void uploadPicture(picture);

    setPictures((pics) => {
      const pictures = [...pics];
      const index = pictures.findIndex((pic) => {
        return pic.questionId === questionId && pic.placeholderName === placeholderName;
      });

      /**
       * If picture exists remove it and
       * add the new picture in the same place (index).
       * This is to support order between pictures
       * with no 'sortable' identifiers
       */
      if (index !== -1) {
        // If the replaced picture was cover, set the new one as cover as well.
        if (pictures[index].isCover) {
          picture.isCover = true;
        }
        pictures.splice(index, 1);
        pictures.splice(index, 0, picture);
        return [...pictures];
      }

      /**
       * If picture does not exists, push it to the array
       */
      return [...pictures, picture];
    });
  };

  const setCoverPicture = (questionId: string, picture?: PictureUpload) => {
    const questionHasCover = pictures
      .filter((q) => q.questionId === questionId)
      .reduce((prev, curr) => prev || curr.isCover, false);

    if (!questionHasCover && !picture) {
      // Set the first picture available as cover
      setPictures((pics) => {
        const pictures = [...pics];
        const firstPicture = pictures.find((p) => p.questionId === questionId);
        if (firstPicture) firstPicture.isCover = true;
        return pictures;
      });
    } else if (picture) {
      // Set the picture passed as parameter as cover
      setPictures((pics) => {
        const pictures = [...pics];
        const oldCoverPicture = pictures.find((p) => p.questionId === questionId && p.isCover);
        const newCoverPicture = pictures.find((p) => p === picture);
        // TODO: When editing a question, BE has no way to know if the picture is cover or not bc we are not sending anything to keep track of that.
        // That's why oldCoverPicture can be undefined - this functionality will be addressed in a future ticket.
        if (oldCoverPicture?.isCover) delete oldCoverPicture.isCover;
        newCoverPicture.isCover = true;
        return pictures;
      });
    }
  };

  const retryPictureUpload = (photo: PictureUploadFile) => {
    const picture = pictures.find((pic) => {
      return pic.file?.path === photo.path;
    });

    const pictureExists = !!picture;

    if (pictureExists) {
      picture.uploading = true;
      picture.failed = false;

      setPictures((pics) => [...pics]);
      setTimeout(() => void uploadPicture(picture), 0);
    }
  };

  const deletePicture = (photo: PictureUpload) => {
    // If the deleted picture is cover, set the next available picture as cover.
    if (photo.isCover) {
      setPictures((pics) => {
        const pictures = [...pics.filter((p) => p !== photo)];
        const firstPicture = pictures.find((p) => p.questionId === photo.questionId);
        if (firstPicture) firstPicture.isCover = true;
        return pictures;
      });
    } else {
      setPictures((pics) => pics.filter((p) => p !== photo));
    }
  };

  const getQuestionPictures = (pictures: PictureUpload[], questionId: string) => {
    return pictures.filter((p) => p.questionId === questionId);
  };

  const getLastQuestionPictures = (questionId: string) => {
    return lastSubmittedPictures.current?.filter((p) => p.questionId === questionId);
  };

  /**
   * Returns all pictures that failed.
   * @returns - The array of pictures status failed
   */
  const getFailedPictures = (): PictureUpload[] => {
    return pictures?.filter((p) => p.failed);
  };

  /**
   * Given a questionId, it will return all pictures from that question that failed.
   * @param {string} questionId - The question identifier
   * @returns - The array of pictures in the question with status failed
   */
  const getQuestionFailedPictures = (questionId: string): PictureUpload[] => {
    return pictures?.filter((p) => p.questionId === questionId && p.failed);
  };

  const getNumberOfPicturesUploading = () => {
    return pictures?.filter((p) => p.uploading).length;
  };

  const getNumberOfPicturesFailed = () => {
    return pictures?.filter((p) => p.failed).length;
  };

  const retryAllFailedPicturesUpload = () => {
    getFailedPictures()?.forEach((picture) => retryPictureUpload(picture.file));
  };

  const setPicturesFromRetrievedSubmission = (pictures: PictureUpload[]) => {
    setPictures(pictures);
    lastSubmittedPictures.current = pictures;
  };

  useEffect(() => {
    if (!preloadedPictures) {
      return;
    }

    setPictures((pics) => [...pics, ...preloadedPictures]);
  }, [preloadedPictures]);

  return (
    <PicturesContext.Provider
      value={{
        deletePicture,
        getFailedPictures,
        getNumberOfPicturesFailed,
        getNumberOfPicturesUploading,
        getQuestionFailedPictures,
        getQuestionPictures,
        pictures,
        getLastQuestionPictures,
        lastSubmittedPictures,
        restoreSavedPictures,
        updateLastSubmittedPictures,
        retryAllFailedPicturesUpload,
        retryPictureUpload,
        savePicture,
        setCoverPicture,
        setPicturesFromRetrievedSubmission,
      }}
    >
      {children}
    </PicturesContext.Provider>
  );
};

export function usePicturesContext() {
  return React.useContext(PicturesContext);
}
